import { EXCHANGE } from '@/components/icons'
import { Exchange, TradeVenue } from '@/types/generated/common/types'

export type ExchangeKey = keyof typeof EXCHANGE

export const EXCHANGE_LOGO_URLS = {
  ibkr: 'https://cdn.brandfetch.io/idx-RB6m2L/w/400/h/400/theme/dark/icon.jpeg?c=1bxid64Mup7aczewSAYMX&t=1772356379100',
  databento:
    'https://cdn.brandfetch.io/idrJ2hsAh5/w/180/h/180/theme/dark/logo.png?c=1bxid64Mup7aczewSAYMX&t=1779039929703',
  deribit: '/images/icon/deribit.png',
} as const satisfies Partial<Record<ExchangeKey, string>>

export interface ExchangeRegistryEntry {
  key: ExchangeKey
  id?: TradeVenue
  label: string
  icon: (typeof EXCHANGE)[ExchangeKey]
  brandfetchDomain?: string
  /**
   * Brandfetch "theme/light" asset path — Brandfetch's naming refers to the
   * LOGO's own color (a light/white-colored wordmark), not the surface it's
   * placed on. Used when the APP is in dark theme, where the table surface
   * behind the icon is dark and needs a light-colored logo for contrast.
   */
  brandfetchLightThemePath?: string
  /**
   * Brandfetch "theme/dark" asset path — the logo's dark-colored variant.
   * Used when the APP is in light theme, where the table surface is light and
   * a light-colored logo (brandfetchLightThemePath) would be invisible.
   * Falls back to brandfetchLightThemePath when unset, matching the previous
   * (theme-unaware) behavior for exchanges without a documented light-surface
   * contrast issue.
   */
  brandfetchDarkThemePath?: string
  logoUrl?: string
  logoObjectFit?: 'cover' | 'contain'
  supportsAccountCreation?: boolean
}

export interface SupportingExchangeEntry extends ExchangeRegistryEntry {
  id: TradeVenue
  supportsAccountCreation: true
}

export const EXCHANGE_REGISTRY: Record<string, ExchangeRegistryEntry> = {
  binance: {
    key: 'binance',
    id: TradeVenue.TRADE_VENUE_BINANCE,
    label: 'Binance',
    icon: EXCHANGE.binance,
    brandfetchDomain: 'binance.com',
    supportsAccountCreation: true,
  },
  deribit: {
    key: 'deribit',
    id: TradeVenue.TRADE_VENUE_DERIBIT,
    label: 'Deribit',
    icon: EXCHANGE.deribit,
    brandfetchDomain: 'deribit.com',
    logoUrl: EXCHANGE_LOGO_URLS.deribit,
    logoObjectFit: 'contain',
    supportsAccountCreation: true,
  },
  ibkr: {
    key: 'ibkr',
    id: TradeVenue.TRADE_VENUE_IBKR,
    label: 'IBKR',
    icon: EXCHANGE.ibkr,
    logoUrl: EXCHANGE_LOGO_URLS.ibkr,
    supportsAccountCreation: false,
  },
  hyperliquid: {
    key: 'hyperliquid',
    id: TradeVenue.TRADE_VENUE_HYPERLIQUID,
    label: 'Hyperliquid',
    icon: EXCHANGE.hyperliquid,
    brandfetchDomain: 'hyperliquid.xyz',
    supportsAccountCreation: true,
  },
  bybit: {
    key: 'bybit',
    id: TradeVenue.TRADE_VENUE_BYBIT,
    label: 'Bybit',
    icon: EXCHANGE.bybit,
    brandfetchDomain: 'bybit.com',
    // The light (white) wordmark remains legible on OrderX's dark surfaces...
    brandfetchLightThemePath: 'domain/bybit.com/w/800/h/313/theme/light/logo',
    // ...but is invisible on light-theme surfaces, so light app theme needs the
    // dark-colored wordmark variant instead (see ExchangeIcon's theme lookup).
    brandfetchDarkThemePath: 'domain/bybit.com/w/800/h/313/theme/dark/logo',
    logoObjectFit: 'contain',
    supportsAccountCreation: true,
  },
  databento: {
    key: 'databento',
    label: 'Databento',
    icon: EXCHANGE.databento,
    brandfetchDomain: 'databento.com',
    logoUrl: EXCHANGE_LOGO_URLS.databento,
    logoObjectFit: 'contain',
  },
  // The markets behind Databento's GLBX/XNAS/XNYS feeds. Like `databento`, none carries an `id`:
  // they're markets to show a logo for, not venues an account connects to.
  cme: {
    key: 'cme',
    label: 'CME Group',
    icon: EXCHANGE.cme,
    brandfetchDomain: 'cmegroup.com',
  },
  nasdaq: {
    key: 'nasdaq',
    label: 'Nasdaq',
    icon: EXCHANGE.nasdaq,
    brandfetchDomain: 'nasdaq.com',
  },
  nyse: {
    key: 'nyse',
    label: 'NYSE',
    icon: EXCHANGE.nyse,
    brandfetchDomain: 'nyse.com',
  },
  upbit: {
    key: 'upbit',
    id: TradeVenue.TRADE_VENUE_UPBIT,
    label: 'Upbit',
    icon: EXCHANGE.upbit,
    brandfetchDomain: 'upbit.com',
    supportsAccountCreation: true,
  },
  derive: {
    key: 'derive',
    id: TradeVenue.TRADE_VENUE_DERIVE,
    label: 'Derive',
    icon: EXCHANGE.derive,
    brandfetchDomain: 'derive.xyz',
    supportsAccountCreation: true,
  },
  lighter: {
    key: 'lighter',
    id: TradeVenue.TRADE_VENUE_LIGHTER,
    label: 'Lighter',
    icon: EXCHANGE.lighter,
    brandfetchDomain: 'lighter.xyz',
    supportsAccountCreation: true,
  },
  // Toss Securities — the broker; its market data arrives as EXCHANGE_XKRX
  // (Korea Exchange) via the ICE consolidated feed. Unlike the crypto venues,
  // broker and exchange are distinct here: `getExchangeLabel` labels the
  // market "KRX" while this entry labels the account's venue "Toss".
  toss: {
    key: 'toss',
    id: TradeVenue.TRADE_VENUE_TOSS,
    label: 'Toss',
    icon: EXCHANGE.toss,
    brandfetchDomain: 'tossinvest.com',
    supportsAccountCreation: true,
  },
  papertrade: {
    key: 'papertrade',
    id: TradeVenue.TRADE_VENUE_PAPERTRADE,
    label: 'Papertrade',
    icon: EXCHANGE.papertrade,
    brandfetchDomain: 'papertrade.xyz',
    supportsAccountCreation: true,
  },
}

export const SUPPORTING_EXCHANGES = Object.values(EXCHANGE_REGISTRY).filter(
  (exchange): exchange is SupportingExchangeEntry =>
    exchange.supportsAccountCreation === true && exchange.id !== undefined
)

export function getExchangeByKey(exchange: string): ExchangeRegistryEntry | undefined {
  return EXCHANGE_REGISTRY[exchange.toLowerCase() as keyof typeof EXCHANGE_REGISTRY]
}

export function getExchangeByTradeVenue(venue: TradeVenue): ExchangeRegistryEntry | undefined {
  return Object.values(EXCHANGE_REGISTRY).find((exchange) => exchange.id === venue)
}

export function tradeVenueToExchangeIconKey(venue: TradeVenue): ExchangeKey {
  return getExchangeByTradeVenue(venue)?.key ?? 'deribit'
}

// Maps the full `Exchange` enum to a display label for visible UI (e.g. an Exchange
// column). Deliberately separate from `toExchangeString` (lib/proto/util.ts), which
// only covers the 3 OAuth-supported venues and silently falls back to "Deribit" for
// anything else — fine for picking an OAuth venue, but would mislabel rows here.
export function getExchangeLabel(exchange: Exchange): string {
  switch (exchange) {
    case Exchange.EXCHANGE_BINANCE:
      return EXCHANGE_REGISTRY.binance.label
    case Exchange.EXCHANGE_DERIBIT:
      return EXCHANGE_REGISTRY.deribit.label
    case Exchange.EXCHANGE_BYBIT:
      return EXCHANGE_REGISTRY.bybit.label
    case Exchange.EXCHANGE_HYPERLIQUID:
      return EXCHANGE_REGISTRY.hyperliquid.label
    case Exchange.EXCHANGE_UPBIT:
      return EXCHANGE_REGISTRY.upbit.label
    case Exchange.EXCHANGE_DERIVE:
      return EXCHANGE_REGISTRY.derive.label
    case Exchange.EXCHANGE_LIGHTER:
      return EXCHANGE_REGISTRY.lighter.label
    case Exchange.EXCHANGE_PAPERTRADE:
      return EXCHANGE_REGISTRY.papertrade.label
    // The market is the Korea Exchange; Toss is only the broker we route
    // through, so an Exchange column reads "KRX", not the registry's "Toss".
    case Exchange.EXCHANGE_XKRX:
      return 'KRX'
    // Databento venues: the market's own name, not its MIC code.
    case Exchange.EXCHANGE_GLBX:
      return 'CME Globex'
    case Exchange.EXCHANGE_XNAS:
      return 'Nasdaq'
    case Exchange.EXCHANGE_XNYS:
      return 'NYSE'
    case Exchange.EXCHANGE_OPRA:
      return 'OPRA'
    case Exchange.EXCHANGE_UNSPECIFIED:
    default:
      return '-'
  }
}

// Companion to `getExchangeLabel` for rendering the venue's logo (via `ExchangeIcon`,
// which takes a registry key). Returns undefined for venues with no registry/logo
// entry rather than guessing an icon.
export function getExchangeIconKey(exchange: Exchange): ExchangeKey | undefined {
  switch (exchange) {
    case Exchange.EXCHANGE_BINANCE:
      return 'binance'
    case Exchange.EXCHANGE_DERIBIT:
      return 'deribit'
    case Exchange.EXCHANGE_BYBIT:
      return 'bybit'
    case Exchange.EXCHANGE_HYPERLIQUID:
      return 'hyperliquid'
    case Exchange.EXCHANGE_UPBIT:
      return 'upbit'
    case Exchange.EXCHANGE_DERIVE:
      return 'derive'
    case Exchange.EXCHANGE_LIGHTER:
      return 'lighter'
    case Exchange.EXCHANGE_PAPERTRADE:
      return 'papertrade'
    // KRX has no logo of its own in the registry; Toss is the venue we reach it
    // through, so its mark is the meaningful one to show. This also gives
    // `exchangeToTradeVenue` the XKRX -> Toss edge it derives from.
    case Exchange.EXCHANGE_XKRX:
      return 'toss'
    // Databento market-data venues show the market's own logo, so a CME future and a Nasdaq
    // stock don't share one icon. OPRA is the consolidated US options feed, not one exchange
    // with a recognisable mark, so it keeps Databento's. None of these registry entries has an
    // `id`, so `exchangeToTradeVenue` below still returns undefined for all four — this only
    // fills in an icon.
    case Exchange.EXCHANGE_GLBX:
      return 'cme'
    case Exchange.EXCHANGE_XNAS:
      return 'nasdaq'
    case Exchange.EXCHANGE_XNYS:
      return 'nyse'
    case Exchange.EXCHANGE_OPRA:
      return 'databento'
    default:
      return undefined
  }
}

// Companion to getExchangeByTradeVenue, for call sites that need to look up a
// venue's server-driven enablement (GetVenues' VenueInfo.enabled) starting
// from an Exchange rather than a TradeVenue — e.g. gating an instrument-list
// fetch. Derived from getExchangeIconKey/EXCHANGE_REGISTRY (the existing
// Exchange<->TradeVenue source of truth) rather than a second hand-written
// switch, so a new venue only needs registering once. Exchanges with no
// account-creation venue (IBKR/Databento's XNAS, XNYS, GLBX, OPRA) return
// undefined regardless of whether they have an icon key (their `cme`/`nasdaq`/
// `nyse`/`databento` registry entries deliberately carry no `id`) — callers should treat that as
// "not venue-gated" rather than "disabled". See resolveExchangeTradeability in
// lib/utils/account-select.ts for the user-facing "can this be traded" check.
export function exchangeToTradeVenue(exchange: Exchange): TradeVenue | undefined {
  const key = getExchangeIconKey(exchange)
  return key ? EXCHANGE_REGISTRY[key].id : undefined
}

/**
 * Exchanges whose instrument-catalog fetch is manually, statically disabled —
 * independent of GetVenues (service/venue.hook.ts's isExchangeEnabled), which
 * some of these (XNYS/XNAS/GLBX/OPRA) aren't gated by at all since they have no
 * TradeVenue. Use this wherever an instrument-list request gets fired per
 * exchange (features/dashboard/lib/supported-exchanges.ts's
 * CATALOG_FETCHABLE_EXCHANGE_LIST, hooks/use-multi-exchange-instrument-map.ts)
 * so there is exactly one place to flip an exchange back on once its backend
 * catalog is ready, instead of each call site tracking its own list and
 * drifting out of sync.
 *
 * XKRX here means position/order rows on a Toss account will show unresolved
 * instrument metadata (no tick size, no base currency) until this is lifted —
 * accepted tradeoff for not hammering a catalog endpoint that isn't serving
 * data yet.
 */
export const CATALOG_DISABLED_EXCHANGES: ReadonlySet<Exchange> = new Set([
  Exchange.EXCHANGE_XKRX,
  Exchange.EXCHANGE_XNYS,
])
